import { useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { FAVES, BRANDS } from "../concept-content";
import { nextClasses } from "../ClubRichThread";
import { FOLLOW, footerLinks, Signup } from "../ConceptBits";
import { Band, CLUB, EASE, Head, InkHeart, Torn, TornEdge, VIEW, datePart } from "./bits";
import "./SectionsB.css";

/* 04 Favorites, 05 Where I'll be, Brands, 06 Loop, Footer. */

export function Favorites() {
  const [on, setOn] = useState(0);
  return <section id="favorites" className="p2-sec p2-faves" aria-labelledby="p2-faves-title">
    <div className="p2-wrap">
      <div className="p2-row">
        <Head n="04" kicker="Things I use and love" id="p2-faves-title" note={<>tools I’d actually buy again <InkHeart delay={0.9} /></>}>My <span className="p2-blue">favorite</span> things.</Head>
        <Link to="/favorites" className="p2-btn p2-btn-line p2-row-btn">See all my favorites <ArrowRight size={18} aria-hidden="true" /></Link>
      </div>
      <div className="p2-faves-grid">
        {/* An offset color block with the pick taped on top, and a sewing button pinned to the corner. */}
        <div className="p2-faves-board" aria-hidden="true">
          <motion.span className="p2-faves-block" initial={{ opacity: 0, x: -24, rotate: 0 }} whileInView={{ opacity: 1, x: 0, rotate: -4 }} viewport={VIEW} transition={{ duration: 0.7, ease: EASE }} />
          <span className="p2-faves-frame">
            <AnimatePresence mode="popLayout">
              <motion.img key={FAVES[on].img} src={FAVES[on].img} alt="" initial={{ opacity: 0, scale: 0.94, rotate: -4 }} animate={{ opacity: 1, scale: 1, rotate: 2 }} exit={{ opacity: 0, scale: 0.96 }} transition={{ duration: 0.4, ease: EASE }} />
            </AnimatePresence>
          </span>
          <i className="p2-faves-tape" />
          <motion.svg className="p2-faves-button" viewBox="0 0 120 120" initial={{ opacity: 0, rotate: -90, scale: 0.6 }} whileInView={{ opacity: 1, rotate: 12, scale: 1 }} viewport={VIEW} transition={{ delay: 0.4, type: "spring", stiffness: 140, damping: 14 }}>
            <circle cx="60" cy="60" r="56" className="p2-sewbtn-face" />
            <circle cx="60" cy="60" r="44" className="p2-sewbtn-rim" />
            {[[46, 46], [74, 46], [46, 74], [74, 74]].map(([x, y]) => <circle key={`${x}${y}`} cx={x} cy={y} r="7" className="p2-sewbtn-hole" />)}
            <path d="M46 46 L74 74 M74 46 L46 74" className="p2-sewbtn-thread" />
          </motion.svg>
          <p className="p2-caps p2-faves-caps">Tried<br />Tested<br />Loved</p>
        </div>
        <motion.div className="p2-list" initial={{ opacity: 0, y: 30, rotate: 0 }} whileInView={{ opacity: 1, y: 0, rotate: -1.5 }} viewport={VIEW} transition={{ duration: 0.6, ease: EASE }}>
          <p className="p2-pen p2-list-title">Shopping list</p>
          <ol>{FAVES.map((f, i) => <li key={f.name}>
            <Link to="/favorites" className={i === on ? "is-on" : ""} onMouseEnter={() => setOn(i)} onFocus={() => setOn(i)}>
              <span className="p2-list-no">No. 0{i + 1}</span>
              <span className="p2-list-thumb"><img src={f.img} alt="" /></span>
              <span className="p2-list-text"><span className="p2-tag">{f.tag}</span><b>{f.name}</b><span className="p2-pen">{f.why}</span></span>
              <ArrowUpRight size={22} aria-hidden="true" />
            </Link>
          </li>)}</ol>
        </motion.div>
      </div>
      <p className="p2-disclose">Some links are affiliate links. If you buy through them, I may earn a small commission at no extra cost to you.</p>
    </div>
  </section>;
}

export function WhereIllBe() {
  const [next] = nextClasses(1);
  const rows = [
    { to: "/events/stitches-night-out", m: "Oct", d: "20", what: "Stitches Night Out", where: "American Fork, UT", note: "a sewists’ movie night!" },
    { to: CLUB, m: datePart(next, { month: "short" }), d: datePart(next, { day: "numeric" }), what: "Live class · Loose Threads Club", where: "Online · 7 PM MT", note: "bring your machine" },
    { to: "/retreats", m: "Soon", d: "✿", what: "Sewing retreats", where: "In person · a few days away", note: "sew, eat, laugh, repeat" },
  ];
  return <Band color="var(--k-pop)" seed={97} className="p2-where" label="p2-where-title">
    <div className="p2-wrap p2-where-grid">
      <div>
        <Head n="05" kicker="Come say hi" id="p2-where-title">Where I’ll <span className="p2-blue">be.</span></Head>
        <ul className="p2-rows">
          {rows.map((r, i) => <motion.li key={r.what} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={VIEW} transition={{ delay: i * 0.1, duration: 0.5, ease: EASE }}>
            <Link to={r.to}>
              <span className="p2-row-date"><small>{r.m}</small>{r.d}</span>
              <span className="p2-row-what"><b>{r.what}</b><span>{r.where}</span></span>
              <span className="p2-pen p2-row-note">{r.note}</span>
              <span className="p2-row-go"><ArrowRight size={20} aria-hidden="true" /></span>
            </Link>
          </motion.li>)}
        </ul>
      </div>
      <motion.figure className="p2-polaroid p2-where-photo" initial={{ opacity: 0, y: 40, rotate: 10 }} whileInView={{ opacity: 1, y: 0, rotate: 5 }} viewport={VIEW} transition={{ delay: 0.3, type: "spring", stiffness: 120, damping: 15 }}>
        <i className="p2-tape" aria-hidden="true" />
        <span className="p2-polaroid-img"><img src="/events/practical-magic-2.jpg" alt="Practical Magic movie poster for Stitches Night Out" /></span>
        <figcaption className="p2-pen">movie night!! <InkHeart delay={1} /></figcaption>
      </motion.figure>
    </div>
  </Band>;
}

export function Brands() {
  return <section className="p2-brands" aria-labelledby="p2-brands-title">
    <h2 id="p2-brands-title" className="p2-kicker">Brands &amp; friends I’ve worked with</h2>
    <ul>{BRANDS.map(b => <li key={b.name}><img src={b.src} alt={b.name} /></li>)}</ul>
    <Link to="/about" className="p2-textlink">Work with me <ArrowRight size={15} aria-hidden="true" /></Link>
  </section>;
}

/** A fabric swatch cut with pinking shears: zigzag on every side, in real
    pixels so the teeth stay the same size, with a stitched line inside. */
function Pinked() {
  const ref = useRef<SVGSVGElement>(null);
  const [box, setBox] = useState({ w: 760, h: 600 });
  useLayoutEffect(() => {
    const el = ref.current?.parentElement;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setBox({ w: Math.round(e.contentRect.width) || 760, h: Math.round(e.contentRect.height) || 600 }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const { w, h } = box, t = 10, pts: string[] = [];
  const side = (x0: number, y0: number, x1: number, y1: number, nx: number, ny: number) => {
    const len = Math.hypot(x1 - x0, y1 - y0), n = Math.max(2, Math.round(len / (t * 2)));
    for (let i = 0; i < n * 2; i++) {
      const f = i / (n * 2), d = i % 2 ? 0 : t;
      pts.push(`${(x0 + (x1 - x0) * f + nx * d).toFixed(1)},${(y0 + (y1 - y0) * f + ny * d).toFixed(1)}`);
    }
  };
  side(0, 0, w, 0, 0, 1); side(w, 0, w, h, -1, 0); side(w, h, 0, h, 0, -1); side(0, h, 0, 0, 1, 0);
  return <svg ref={ref} className="p2-pinked" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
    <polygon points={pts.join(" ")} />
    <rect x={t + 14} y={t + 14} width={Math.max(0, w - 2 * (t + 14))} height={Math.max(0, h - 2 * (t + 14))} />
  </svg>;
}

export function Loop() {
  return <section id="newsletter" className="p2-sec p2-loop" aria-labelledby="p2-loop-title">
    <svg className="p2-loop-paper" viewBox="0 0 1600 700" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <Torn seed={101} fill="var(--k-strong)" edge={[[0, 430], [120, 470], [230, 560], [290, 700]]} close={[[0, 700]]} />
      <Torn seed={113} fill="var(--k-pop)" edge={[[1600, 120], [1500, 110], [1400, 60], [1350, 0]]} close={[[1600, 0]]} />
    </svg>
    <svg className="p2-loop-dash" viewBox="0 0 1600 700" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <path d="M40 300 C 120 250, 220 300, 260 380 S 300 520, 420 560" />
      <path d="M1580 300 C 1500 340, 1440 300, 1400 240 S 1300 150, 1200 170" />
    </svg>
    <motion.div className="p2-loop-swatch" initial={{ opacity: 0, y: 40, rotate: 0 }} whileInView={{ opacity: 1, y: 0, rotate: -1 }} viewport={VIEW} transition={{ duration: 0.7, ease: EASE }}>
      <Pinked />
      <i className="p2-loop-tape p2-loop-tape-l" aria-hidden="true" /><i className="p2-loop-tape p2-loop-tape-r" aria-hidden="true" />
      <p className="p2-kicker"><span>06</span>Letters from the sewing room</p>
      <h2 id="p2-loop-title">One email a week. <span className="p2-blue">Mostly fabric.</span></h2>
      <p className="p2-loop-lede">What I’m making, a helpful tip, and first dibs on retreats and events.</p>
      <Signup source="home-pattern-2" className="p2-signup" button={<>Sign me up <ArrowRight size={18} aria-hidden="true" /></>} done={<>You’re in. See you in your inbox! <span aria-hidden="true">♡</span></>} />
      <p className="p2-sig-name p2-loop-sig">Chelsea</p>
    </motion.div>
  </section>;
}

export function Footer() {
  return <footer className="p2-foot">
    <TornEdge fill="var(--k-foot)" seed={131} side="top" />
    <div className="p2-wrap p2-foot-grid">
      <div>
        <Link to="/" className="p2-logo p2-foot-logo">A Few<br />Loose Threads</Link>
        <p className="p2-pen p2-foot-pen">Color outside the pattern. <InkHeart delay={0.6} /></p>
      </div>
      <nav aria-label="Footer"><ul>{footerLinks(CLUB).map(l => <li key={l.href}><Link to={l.href}>{l.label}</Link></li>)}</ul></nav>
      <div>
        <p className="p2-kicker">Follow along</p>
        <ul className="p2-foot-social">{FOLLOW.map(f => <li key={f.s.key}><a href={f.s.url} target="_blank" rel="noreferrer" aria-label={`${f.s.platform} ${f.s.handle}`}>{f.icon}</a></li>)}</ul>
      </div>
    </div>
    <p className="p2-foot-fine">© {new Date().getFullYear()} A Few Loose Threads · Bolder, brighter, happier, handmade.</p>
  </footer>;
}
